import { Switch, Select, MultiSelect, Stack, Group } from '@forwardflow/ui-v2'
import { NumberInput } from '@mantine/core'
import { TimeInput } from '@mantine/dates'
import { useEffect, useState } from 'react'
import OrganizationService from '@forwardflow/services/OrganizationService'
import {
   IAriaRecipient,
   IAutomationsConfig,
   IRecurrence,
   ISyncConfig,
   RecurrenceType,
} from '@forwardflow/types/automations'

const RECURRENCE_OPTIONS = [
   { value: 'daily', label: 'Daily' },
   { value: 'weekly', label: 'Weekly' },
   { value: 'interval', label: 'Every N days' },
]

const DAYS_OF_WEEK_OPTIONS = [
   { value: 'sun', label: 'Sunday' },
   { value: 'mon', label: 'Monday' },
   { value: 'tue', label: 'Tuesday' },
   { value: 'wed', label: 'Wednesday' },
   { value: 'thu', label: 'Thursday' },
   { value: 'fri', label: 'Friday' },
   { value: 'sat', label: 'Saturday' },
]

const defaultRecurrence = (type: RecurrenceType): IRecurrence => {
   if (type === 'weekly') return { type: 'weekly', daysOfWeek: [] }
   if (type === 'interval') {
      const today = new Date().toISOString().split('T')[0]
      return { type: 'interval', intervalDays: 1, anchorDate: today }
   }
   return { type: 'daily' }
}

interface SyncSlotFormProps {
   label: string
   value: ISyncConfig | null
   enabled: boolean
   disabled: boolean
   onToggle: (enabled: boolean) => void
   onChange: (value: ISyncConfig) => void
}

const SyncSlotForm = ({
   label,
   value,
   enabled,
   disabled,
   onToggle,
   onChange,
}: SyncSlotFormProps) => {
   const recurrenceType: RecurrenceType = value?.recurrence?.type ?? 'daily'

   const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const time = e.target.value
      onChange({
         time,
         recurrence: value?.recurrence ?? { type: 'daily' },
      })
   }

   const handleRecurrenceTypeChange = (val: string | null) => {
      if (!val) return
      const type = val as RecurrenceType
      onChange({
         time: value?.time ?? '',
         recurrence: defaultRecurrence(type),
      })
   }

   const handleDaysChange = (days: string[]) => {
      onChange({
         time: value?.time ?? '',
         recurrence: { type: 'weekly', daysOfWeek: days },
      })
   }

   const handleIntervalDaysChange = (val: string | number) => {
      const n = typeof val === 'string' ? parseInt(val, 10) : val
      const existing = value?.recurrence
      const anchorDate =
         existing?.type === 'interval'
            ? existing.anchorDate
            : new Date().toISOString().split('T')[0]
      onChange({
         time: value?.time ?? '',
         recurrence: {
            type: 'interval',
            intervalDays: isNaN(n) ? 1 : Math.max(1, n),
            anchorDate,
         },
      })
   }

   return (
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-5">
         <Group className="items-center justify-between mb-4">
            <p className="text-white text-sm font-medium">{label}</p>
            <Switch
               checked={enabled}
               disabled={disabled}
               onChange={(e) => onToggle(e.currentTarget.checked)}
            />
         </Group>

         {enabled && (
            <Stack gap={16}>
               <div>
                  <p className="text-white/90 text-sm font-medium mb-1.5">
                     Time
                  </p>
                  <TimeInput
                     value={value?.time ?? ''}
                     disabled={disabled}
                     onChange={handleTimeChange}
                     classNames={{
                        input: 'bg-white/[0.02] border border-white/10 text-white rounded-lg h-11 px-3',
                        wrapper: 'w-full',
                     }}
                  />
               </div>

               <Select
                  label="Recurrence"
                  data={RECURRENCE_OPTIONS}
                  value={recurrenceType}
                  disabled={disabled}
                  onChange={handleRecurrenceTypeChange}
               />

               {recurrenceType === 'weekly' && (
                  <MultiSelect
                     label="Days of week"
                     data={DAYS_OF_WEEK_OPTIONS}
                     value={
                        value?.recurrence?.type === 'weekly'
                           ? value.recurrence.daysOfWeek
                           : []
                     }
                     disabled={disabled}
                     onChange={handleDaysChange}
                  />
               )}

               {recurrenceType === 'interval' && (
                  <div>
                     <p className="text-white/90 text-sm font-medium mb-1.5">
                        Repeat every N days
                     </p>
                     <NumberInput
                        min={1}
                        value={
                           value?.recurrence?.type === 'interval'
                              ? value.recurrence.intervalDays
                              : 1
                        }
                        disabled={disabled}
                        onChange={handleIntervalDaysChange}
                        classNames={{
                           input: 'bg-white/[0.02] border border-white/10 text-white rounded-lg h-11 px-3',
                           wrapper: 'w-full',
                        }}
                     />
                     <p className="text-white/40 text-xs mt-1.5">
                        Counts from today.
                     </p>
                  </div>
               )}
            </Stack>
         )}
      </div>
   )
}

export interface IAutomationsFormValue {
   isEnabled: boolean
   recipientEmails: string[]
   morningSyncEnabled: boolean
   morningSync: ISyncConfig
   dayEndSyncEnabled: boolean
   dayEndSync: ISyncConfig
   // Event-driven toggles (plan 0005) — on/off only, no schedule.
   appointmentSetEnabled: boolean
   callEndedEnabled: boolean
}

// Morning sync pre-fills to 8:00 AM (08:00), daily, when nothing is persisted.
const DEFAULT_MORNING_SYNC: ISyncConfig = {
   isEnabled: false,
   time: '08:00',
   recurrence: { type: 'daily' },
}

// Day-end sync pre-fills to 5:00 PM (17:00), daily, when nothing is persisted.
const DEFAULT_DAY_END_SYNC: ISyncConfig = {
   isEnabled: false,
   time: '17:00',
   recurrence: { type: 'daily' },
}

export const automationsConfigToFormValue = (
   config: IAutomationsConfig | null | undefined
): IAutomationsFormValue => ({
   isEnabled: config?.isEnabled ?? false,
   recipientEmails: config?.recipientEmails ?? [],
   // The per-slot toggle comes from the slot's own `isEnabled`; the slot config
   // (time/recurrence) is kept regardless so toggling off never loses it.
   morningSyncEnabled: config?.morningSync?.isEnabled ?? false,
   morningSync: config?.morningSync ?? { ...DEFAULT_MORNING_SYNC },
   dayEndSyncEnabled: config?.dayEndSync?.isEnabled ?? false,
   dayEndSync: config?.dayEndSync ?? { ...DEFAULT_DAY_END_SYNC },
   // Event toggles default OFF when unset.
   appointmentSetEnabled: config?.appointmentSet?.isEnabled ?? false,
   callEndedEnabled: config?.callEnded?.isEnabled ?? false,
})

export const formValueToAutomationsPayload = (
   form: IAutomationsFormValue
): Partial<IAutomationsConfig> => {
   // Send the full slot with its per-slot `isEnabled` so disabling a slot keeps
   // its time/recurrence (it just won't fire) instead of resetting to default.
   return {
      isEnabled: form.isEnabled,
      recipientEmails: form.recipientEmails,
      morningSync: {
         ...form.morningSync,
         isEnabled: form.morningSyncEnabled,
      },
      dayEndSync: {
         ...form.dayEndSync,
         isEnabled: form.dayEndSyncEnabled,
      },
      // Event-driven toggles (plan 0005) — on/off only.
      appointmentSet: { isEnabled: form.appointmentSetEnabled },
      callEnded: { isEnabled: form.callEndedEnabled },
   }
}

export const validateAutomationsForm = (
   form: IAutomationsFormValue
): string | null => {
   const validateSlot = (
      label: string,
      enabled: boolean,
      slot: ISyncConfig
   ): string | null => {
      if (!enabled) return null
      if (!slot.time) return `${label}: time is required`
      if (slot.recurrence.type === 'weekly') {
         if (slot.recurrence.daysOfWeek.length === 0)
            return `${label}: select at least one day for weekly recurrence`
      }
      if (slot.recurrence.type === 'interval') {
         if (!slot.recurrence.intervalDays || slot.recurrence.intervalDays < 1)
            return `${label}: interval days must be at least 1`
         if (!slot.recurrence.anchorDate)
            return `${label}: starting date is required for interval recurrence`
      }
      return null
   }

   if (form.recipientEmails.length === 0)
      return 'Select at least one recipient'

   return (
      validateSlot('Morning sync', form.morningSyncEnabled, form.morningSync) ??
      validateSlot('Day-end sync', form.dayEndSyncEnabled, form.dayEndSync)
   )
}

interface EventToggleRowProps {
   label: string
   description?: string
   checked: boolean
   disabled: boolean
   onToggle: (enabled: boolean) => void
}

// A simple on/off row for event-driven automations (no time/recurrence).
const EventToggleRow = ({
   label,
   description,
   checked,
   disabled,
   onToggle,
}: EventToggleRowProps) => (
   <div className="rounded-lg border border-white/10 bg-white/[0.02] p-5">
      <Group className="items-center justify-between">
         <Stack gap={4}>
            <p className="text-white text-sm font-medium">{label}</p>
            {description && (
               <p className="text-white/50 text-xs">{description}</p>
            )}
         </Stack>
         <Switch
            checked={checked}
            disabled={disabled}
            onChange={(e) => onToggle(e.currentTarget.checked)}
         />
      </Group>
   </div>
)

interface AutomationsFormProps {
   value: IAutomationsFormValue
   disabled: boolean
   onChange: (value: IAutomationsFormValue) => void
}

export const AutomationsForm = ({
   value,
   disabled,
   onChange,
}: AutomationsFormProps) => {
   const update = (partial: Partial<IAutomationsFormValue>) => {
      onChange({ ...value, ...partial })
   }

   // Accepted-invitation users — the recipient picker source (plan 0007).
   const [recipients, setRecipients] = useState<IAriaRecipient[]>([])
   useEffect(() => {
      let cancelled = false
      OrganizationService.getAutomationRecipients()
         .then((list) => {
            if (!cancelled) setRecipients(list)
         })
         .catch((e) => {
            if (!cancelled) console.error('Failed to load recipients:', e)
         })
      return () => {
         cancelled = true
      }
   }, [])

   // Options keyed by email; show name + email so duplicates are distinguishable.
   const recipientOptions = recipients
      .filter((u) => !!u.email)
      .map((u) => ({
         value: (u.email as string).toLowerCase(),
         label: u.name ? `${u.name} (${u.email})` : (u.email as string),
      }))

   return (
      <Stack gap={24} className="p-6">
         <div className="rounded-lg border border-white/10 bg-white/[0.02] p-5">
            <Group className="items-center justify-between">
               <Stack gap={4}>
                  <p className="text-white text-sm font-medium">
                     Enable daily sync automations
                  </p>
                  <p className="text-white/50 text-xs">
                     Run scheduled syncs to keep your CRM data up to date
                  </p>
               </Stack>
               <Switch
                  checked={value.isEnabled}
                  disabled={disabled}
                  onChange={(e) =>
                     update({ isEnabled: e.currentTarget.checked })
                  }
               />
            </Group>

            <div className="mt-5">
               <p className="text-white/90 text-sm font-medium mb-1.5">
                  Send automation SMS to
               </p>
               <MultiSelect
                  data={recipientOptions}
                  value={value.recipientEmails}
                  disabled={disabled || !value.isEnabled}
                  placeholder="Select users"
                  searchable
                  onChange={(emails) => update({ recipientEmails: emails })}
               />
            </div>
         </div>

         {/* When the master switch is off, every automation below is disabled —
             grey them out to make that relationship clear. */}
         <SyncSlotForm
            label="Morning sync"
            value={value.morningSync}
            enabled={value.morningSyncEnabled}
            disabled={disabled || !value.isEnabled}
            onToggle={(enabled) => update({ morningSyncEnabled: enabled })}
            onChange={(morningSync) => update({ morningSync })}
         />

         <SyncSlotForm
            label="Day-end sync"
            value={value.dayEndSync}
            enabled={value.dayEndSyncEnabled}
            disabled={disabled || !value.isEnabled}
            onToggle={(enabled) => update({ dayEndSyncEnabled: enabled })}
            onChange={(dayEndSync) => update({ dayEndSync })}
         />

         {/* Event-driven automations (plan 0005) — fire on a real event, so just
             on/off. Active conversation replies are always on (no toggle). */}
         <EventToggleRow
            label="Appointment Reminder"
            checked={value.appointmentSetEnabled}
            disabled={disabled || !value.isEnabled}
            onToggle={(enabled) => update({ appointmentSetEnabled: enabled })}
         />

         {/* Call ended is hidden for now — re-enable this block to bring it back.
             The toggle still saves (defaults off) via callEndedEnabled.
         <EventToggleRow
            label="Call ended"
            description="Notify when a call wraps up"
            checked={value.callEndedEnabled}
            disabled={disabled || !value.isEnabled}
            onToggle={(enabled) => update({ callEndedEnabled: enabled })}
         />
         */}
      </Stack>
   )
}
